/**
 * Custom bundle cart helpers: remove group / change group quantity.
 * Works with flat `_bundle_id` line items; dispatches Horizon `cart:update`.
 * Bundle qty scales each line by `_bundle_component_qty` (e.g. set qty 3 × component 2 → 6).
 *
 * @see docs/dev-custom-bundle-plan.md
 */
import { CartUpdateEvent } from '@theme/events';

/** @type {WeakSet<HTMLElement>} */
const updatingGroups = new WeakSet();

/**
 * @param {HTMLElement | null} group
 * @param {string} msg
 */
function showBundleError(group, msg) {
  const el = group?.querySelector('[data-dev-bundle-error]');
  const cell = group?.querySelector('[data-dev-bundle-error-cell]');
  if (!(el instanceof HTMLElement)) return;

  el.textContent = msg ?? '';
  const hasMsg = Boolean(msg);

  if (cell instanceof HTMLElement) {
    cell.toggleAttribute('hidden', !hasMsg);
    cell.classList.toggle('hidden', !hasMsg);
  }
}

/**
 * @param {HTMLElement} group
 * @param {boolean} busy
 */
function setGroupBusy(group, busy) {
  group.toggleAttribute('data-dev-bundle-updating', busy);

  const remove = group.querySelector('[data-dev-bundle-remove]');
  if (remove instanceof HTMLButtonElement) remove.disabled = busy;

  const input = group.querySelector('[data-dev-bundle-qty-input]');
  if (input instanceof HTMLInputElement) input.disabled = busy;

  if (busy) {
    const minus = group.querySelector('[data-dev-bundle-qty-minus]');
    const plus = group.querySelector('[data-dev-bundle-qty-plus]');
    if (minus instanceof HTMLButtonElement) minus.disabled = true;
    if (plus instanceof HTMLButtonElement) plus.disabled = true;
  } else {
    syncQtyControls(group);
  }
}

/**
 * @param {HTMLElement} group
 */
function syncQtyControls(group) {
  const input = group.querySelector('[data-dev-bundle-qty-input]');
  if (!(input instanceof HTMLInputElement)) return;

  const qty = Math.max(1, parseInt(input.value, 10) || 1);
  const max = getMaxBundleQty(group);
  input.value = String(qty);
  input.max = String(max);

  const minus = group.querySelector('[data-dev-bundle-qty-minus]');
  const plus = group.querySelector('[data-dev-bundle-qty-plus]');
  // Minus stays enabled at qty 1 — next click removes the whole bundle group
  if (minus instanceof HTMLButtonElement) minus.disabled = false;
  if (plus instanceof HTMLButtonElement) plus.disabled = qty >= max;
}

/**
 * @param {HTMLElement} group
 * @returns {number}
 */
function getMaxBundleQty(group) {
  const max = parseInt(group.getAttribute('data-max-bundle-qty') || '99', 10);
  return Number.isFinite(max) && max > 0 ? max : 99;
}

/**
 * @param {HTMLElement} group
 * @returns {{ updates: Record<string, number>, lineKeys: Record<string, true> }}
 */
function collectLineKeys(group) {
  /** @type {Record<string, number>} */
  const updates = {};
  /** @type {Record<string, true>} */
  const lineKeys = {};
  for (const line of group.querySelectorAll('[data-dev-cart-bundle-component]')) {
    if (!(line instanceof HTMLElement)) continue;
    const key = line.getAttribute('data-line-key');
    if (key) {
      lineKeys[key] = true;
      updates[key] = 0;
    }
  }
  return { updates, lineKeys };
}

/**
 * @param {Record<string, number>} updates
 * @param {HTMLElement | null} group
 * @returns {Promise<boolean>}
 */
async function bundleCartUpdate(updates, group) {
  showBundleError(group, '');

  if (group instanceof HTMLElement) {
    if (updatingGroups.has(group)) return false;
    updatingGroups.add(group);
    setGroupBusy(group, true);
  }

  const sectionIds = [];
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      sectionIds.push(el.dataset.sectionId);
    }
  }

  const updateUrl =
    (typeof Theme !== 'undefined' && Theme.routes?.cart_update_url) ||
    `${window.Shopify?.routes?.root || '/'}cart/update.js`;
  const cartUrl = `${window.Shopify?.routes?.root || '/'}cart.js`;

  /** @type {{ items?: Array<Record<string, unknown>> } | null} */
  let prevCart = null;
  try {
    const prevRes = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
    if (prevRes.ok) prevCart = await prevRes.json();
  } catch {
    prevCart = null;
  }

  try {
    const res = await fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await res.json();

    if (data.status && data.status !== 200) {
      const fallback =
        group?.getAttribute('data-update-error-message') || 'Update error.';
      showBundleError(group, data.description || data.message || fallback);
      return false;
    }

    showBundleError(group, '');

    emitBundleCartAnalytics(group, updates, prevCart?.items || [], data.items || []);

    document.dispatchEvent(
      new CartUpdateEvent(data, 'dev-bundle-cart', {
        source: 'dev-bundle-cart',
        itemCount: data.item_count,
        sections: data.sections,
      })
    );

    return true;
  } catch {
    showBundleError(
      group,
      group?.getAttribute('data-connection-error-message') || 'Connection error.'
    );
    return false;
  } finally {
    if (group instanceof HTMLElement) {
      updatingGroups.delete(group);
      if (document.contains(group)) setGroupBusy(group, false);
    }
  }
}

/**
 * One multi-item dataLayer event per bundle qty/remove (not N× line events).
 * @param {HTMLElement | null} group
 * @param {Record<string, number>} updates
 * @param {Array<Record<string, unknown>>} prevItems
 * @param {Array<Record<string, unknown>>} nextItems
 */
function emitBundleCartAnalytics(group, updates, prevItems, nextItems) {
  const title = group?.getAttribute('data-bundle-title') || '';
  const keySet = new Set(Object.keys(updates));
  if (!keySet.size) return;

  const prevLines = prevItems.filter((item) => keySet.has(String(item.key)));
  const nextLines = nextItems.filter((item) => keySet.has(String(item.key)));
  const allZero = Object.values(updates).every((qty) => qty === 0);

  if (allZero && prevLines.length) {
    window.bundleHandleRemoveFromCart?.(prevLines, title);
    return;
  }

  let prevSum = 0;
  let nextSum = 0;
  for (const line of prevLines) prevSum += Number(line.quantity) || 0;
  for (const line of nextLines) nextSum += Number(line.quantity) || 0;

  if (nextSum > prevSum) {
    const deltaItems = nextLines
      .map((line) => {
        const prev = prevLines.find((p) => String(p.key) === String(line.key));
        const delta = (Number(line.quantity) || 0) - (Number(prev?.quantity) || 0);
        return delta > 0 ? { ...line, quantity: delta } : null;
      })
      .filter(Boolean);
    if (deltaItems.length) window.bundleHandleCartQtyUp?.(deltaItems, title);
    return;
  }

  if (nextSum < prevSum) {
    const deltaItems = prevLines
      .map((line) => {
        const next = nextLines.find((n) => String(n.key) === String(line.key));
        const delta = (Number(line.quantity) || 0) - (Number(next?.quantity) || 0);
        return delta > 0 ? { ...line, quantity: delta } : null;
      })
      .filter(Boolean);
    if (deltaItems.length) window.bundleHandleRemoveFromCart?.(deltaItems, title);
  }
}

/**
 * @param {HTMLElement} btn
 */
async function bundleRemove(btn) {
  const group = btn.closest('[data-dev-cart-bundle]');
  if (!(group instanceof HTMLElement)) return;
  const { updates } = collectLineKeys(group);
  if (!Object.keys(updates).length) return;
  await bundleCartUpdate(updates, group);
}

/**
 * @param {HTMLInputElement} input
 * @param {number} [requestedQty]
 */
async function bundleQtyChange(input, requestedQty) {
  const group = input.closest('[data-dev-cart-bundle]');
  if (!(group instanceof HTMLElement)) return;

  const committed = parseInt(input.getAttribute('data-dev-bundle-qty-committed') || input.value, 10) || 1;
  const max = getMaxBundleQty(group);
  const rawQty = requestedQty ?? (parseInt(input.value, 10) || 1);

  // Typing / setting 0 removes the whole bundle group (same as classic cart lines)
  if (rawQty <= 0) {
    const removeBtn = group.querySelector('[data-dev-bundle-remove]');
    if (removeBtn instanceof HTMLElement) {
      await bundleRemove(removeBtn);
    } else {
      const { updates } = collectLineKeys(group);
      if (Object.keys(updates).length) await bundleCartUpdate(updates, group);
    }
    return;
  }

  let newQty = Math.max(1, rawQty);

  if (newQty > max) {
    input.value = String(committed);
    syncQtyControls(group);
    showBundleError(
      group,
      group.getAttribute('data-insufficient-stock-message') || 'Not enough stock.'
    );
    return;
  }

  input.value = String(newQty);
  syncQtyControls(group);

  if (newQty === committed) return;

  /** @type {Record<string, number>} */
  const updates = {};
  for (const line of group.querySelectorAll('[data-dev-cart-bundle-component]')) {
    if (!(line instanceof HTMLElement)) continue;
    const key = line.getAttribute('data-line-key');
    const cq = parseInt(line.getAttribute('data-component-qty') || '1', 10) || 1;
    if (key) updates[key] = cq * newQty;
  }
  if (!Object.keys(updates).length) return;

  const ok = await bundleCartUpdate(updates, group);
  if (ok) {
    if (document.contains(input)) {
      input.setAttribute('data-dev-bundle-qty-committed', String(newQty));
    }
  } else if (document.contains(input)) {
    input.value = String(committed);
    syncQtyControls(group);
  }
}

/**
 * Event delegation — survives cart morph / section re-renders.
 * @param {Event} event
 */
function onClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const removeBtn = target.closest('[data-dev-bundle-remove]');
  if (removeBtn instanceof HTMLElement) {
    event.preventDefault();
    bundleRemove(removeBtn);
    return;
  }

  const minus = target.closest('[data-dev-bundle-qty-minus]');
  if (minus instanceof HTMLElement) {
    event.preventDefault();
    const group = minus.closest('[data-dev-cart-bundle]');
    if (!(group instanceof HTMLElement) || updatingGroups.has(group)) return;
    const input = group.querySelector('[data-dev-bundle-qty-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const current = parseInt(input.value, 10) || 1;
    if (current <= 1) {
      bundleRemove(minus);
      return;
    }

    bundleQtyChange(input, current - 1);
    return;
  }

  const plus = target.closest('[data-dev-bundle-qty-plus]');
  if (plus instanceof HTMLElement) {
    event.preventDefault();
    const group = plus.closest('[data-dev-cart-bundle]');
    if (!(group instanceof HTMLElement) || updatingGroups.has(group)) return;
    const input = group.querySelector('[data-dev-bundle-qty-input]');
    if (input instanceof HTMLInputElement) {
      const next = (parseInt(input.value, 10) || 1) + 1;
      bundleQtyChange(input, next);
    }
  }
}

/**
 * @param {Event} event
 */
function onChange(event) {
  const target = event.target;
  if (target instanceof HTMLInputElement && target.hasAttribute('data-dev-bundle-qty-input')) {
    const group = target.closest('[data-dev-cart-bundle]');
    if (group instanceof HTMLElement && updatingGroups.has(group)) return;
    bundleQtyChange(target);
  }
}

if (!window.__devBundleCartBound) {
  window.__devBundleCartBound = true;
  document.addEventListener('click', onClick);
  document.addEventListener('change', onChange);
}
